import { DialogComponent } from '@theme/dialog';

/**
 * Auto-opening newsletter popup.
 *
 * Extends the theme's dialog component (modal behavior, scroll-lock,
 * click-outside and Escape handling) and adds:
 *   - Trigger logic: delay / scroll depth / exit-intent / immediate.
 *   - Frequency capping via localStorage / sessionStorage.
 *   - Re-opening after a submission to surface success or error messages.
 *   - Theme editor mode: stays permanently open and cannot be closed, so the
 *     merchant can style it without it disappearing.
 *
 * Configuration is read from data-* attributes set in the Liquid block.
 */
class NewsletterPopup extends DialogComponent {
  #timer = null;

  // Capture the parent close handler so we can disable closing in the editor.
  #parentCloseDialog = this.closeDialog;

  /** @returns {Promise<void>} */
  closeDialog = async () => {
    // Keep the popup visible while editing in the theme editor.
    if (this.#isEditor) return;
    return this.#parentCloseDialog();
  };

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('dialog:open', this.#handleDialogOpen);
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timer) clearTimeout(this.#timer);
    document.removeEventListener('mouseout', this.#handleExitIntent);
    window.removeEventListener('scroll', this.#handleScroll);
  }

  /** Whether we are inside the theme editor preview. */
  get #isEditor() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  get #delayMs() {
    return Math.max(0, Number(this.dataset.delay || 3)) * 1000;
  }

  /** Storage key, unique per block instance. */
  get #key() {
    return `theme:newsletter-popup:${this.dataset.storageKey || this.id || 'default'}`;
  }

  #setup() {
    // Theme editor: always visible, no triggers, no frequency capping.
    if (this.#isEditor) {
      requestAnimationFrame(() => {
        const { dialog } = this.refs;
        if (!dialog.open) dialog.showModal();
        dialog.addEventListener('cancel', this.#preventCancel);
        this.#focusHeading();
      });
      return;
    }

    // After a form submission the page reloads: re-open to show the result.
    if (this.querySelector('[data-newsletter-error]')) {
      this.showDialog();
      return;
    }
    if (this.querySelector('[data-newsletter-success]')) {
      this.#remember(true);
      this.showDialog();
      return;
    }

    // Respect the "disable on mobile" setting.
    if (this.dataset.mobileEnabled === 'false' && window.matchMedia('(max-width: 749px)').matches) {
      return;
    }

    if (!this.#canShow()) return;

    switch (this.dataset.trigger) {
      case 'immediate':
        this.#trigger();
        break;

      case 'scroll':
        window.addEventListener('scroll', this.#handleScroll, { passive: true });
        break;

      case 'exit':
        // Exit-intent needs a pointer; fall back to a delay on touch devices.
        if (window.matchMedia('(hover: none)').matches) {
          this.#timer = setTimeout(() => this.#trigger(), this.#delayMs);
        } else {
          document.addEventListener('mouseout', this.#handleExitIntent);
        }
        break;

      case 'delay':
      default:
        this.#timer = setTimeout(() => this.#trigger(), this.#delayMs);
    }
  }

  /** @param {Event} event */
  #preventCancel = (event) => event.preventDefault();

  /** Move initial focus to the heading so the close button isn't focus-ringed on open. */
  #handleDialogOpen = () => this.#focusHeading();

  #focusHeading() {
    // Honor an explicit autofocus (e.g. the email field when there are errors).
    if (this.querySelector('[autofocus]')) return;
    const heading = /** @type {HTMLElement | null} */ (
      this.refs.dialog.querySelector('[data-dialog-focus]')
    );
    if (heading) heading.focus({ preventScroll: true });
  }

  #handleScroll = () => {
    const scrolled =
      ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100;

    if (scrolled >= Number(this.dataset.scroll || 30)) {
      window.removeEventListener('scroll', this.#handleScroll);
      this.#trigger();
    }
  };

  #handleExitIntent = (event) => {
    if (event.clientY <= 0 && !event.relatedTarget) {
      document.removeEventListener('mouseout', this.#handleExitIntent);
      this.#trigger();
    }
  };

  #trigger() {
    if (this.refs.dialog.open) return;
    this.showDialog();
    this.#remember(false);
  }

  /** Decide whether the popup may be shown based on the frequency setting. */
  #canShow() {
    // Once subscribed, never show again (any frequency mode).
    try {
      const persisted = JSON.parse(localStorage.getItem(this.#key) || 'null');
      if (persisted && persisted.subscribed) return false;
    } catch (e) {
      /* storage unavailable */
    }

    const frequency = this.dataset.frequency || 'days';

    if (frequency === 'always') return true;

    if (frequency === 'session') {
      try {
        return sessionStorage.getItem(this.#key) !== 'seen';
      } catch (e) {
        return true;
      }
    }

    // frequency === 'days'
    try {
      const raw = localStorage.getItem(this.#key);
      if (!raw) return true;
      const data = JSON.parse(raw);
      const days = Number(this.dataset.days || 7);
      return Date.now() - data.at > days * 86400000;
    } catch (e) {
      return true;
    }
  }

  /** Persist that the popup was shown (or that the user subscribed). */
  #remember(subscribed) {
    const frequency = this.dataset.frequency || 'days';
    try {
      if (frequency === 'session' && !subscribed) {
        sessionStorage.setItem(this.#key, 'seen');
      } else {
        localStorage.setItem(
          this.#key,
          JSON.stringify({ at: Date.now(), subscribed: Boolean(subscribed) })
        );
      }
    } catch (e) {
      /* storage unavailable */
    }
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
